import type { AnnulOperation } from '@festina/configuration-general-types/configuration'
import {
  type BranchKey,
  LayerKey,
  OperationLayerSet,
  STANDARD_BRANCH,
  type WorkspaceKey,
} from '@festina-life/api'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'
import { type PropsWithChildren, useCallback, useMemo } from 'react'

import { conciseWorkspaceToWorkspaceKey } from '../components/branchesPanel/branchDetails/branchWorkspaces/helpers'
import { useMetaModelContext } from '../context/metaModelContext'
import { useBaseBranchWorkspaces } from '../hooks/useBaseBranchWorkspaces'
import { useNavigationParams } from '../system/useNavigationParams'
import { buildAnnulOperationsByKey } from '../uiModels/annulOperations/helpers'
import { isEqualBranch } from '../uiModels/branches/helpers'
import { useConciseBranch } from '../uiModels/branches/useConciseBranch'
import { useChangesetsWithHistory } from '../uiModels/changesets/useChangesetsWithHistory'
import { useConciseChangesets } from '../uiModels/changesets/useConciseChangesets'
import { useConfiguredEnums } from '../uiModels/enumLiterals/useConfiguredEnums'
import { useLocationErrors } from '../uiModels/errors/useLocationErrors'
import { constructIndexedGlMapOperations } from '../uiModels/glMapOperations/helpers'
import {
  constructIndexedParameterDefinitionCreateOperations,
  constructIndexedParameterValueOperations,
} from '../uiModels/parameterOperations/helpers'
import { constructIndexedReconciliationSpecOperations } from '../uiModels/reconciliationSpecOperations/helpers'
import { constructIndexedRegisteredTenantCreateOperations } from '../uiModels/registeredTenantCreateOperations/helpers'
import { constructIndexedTemplateOperations } from '../uiModels/templateOperations/helpers'
import {
  type IndexedTemplateOperationWithMetaData,
  isWorkspaceAnnulOperation,
  isWorkspaceGlMapOperation,
  isWorkspaceParameterDefinitionOperation,
  isWorkspaceParameterValueAmendOperation,
  isWorkspaceParameterValueOperation,
  isWorkspaceReconciliationSpecCreateOperation,
  isWorkspaceRegisteredTenantOperation,
  isWorkspaceTemplateOperation,
} from '../uiModels/templateOperations/types'
import type { WorkspaceOperationOf } from '../uiModels/types'
import type { ChangeOperation } from '../uiModels/workspace/types'
import { useConfigurationComponentWorkspaces } from '../uiModels/workspace/useConfigurationComponentWorkspaces'
import { useCreateAutomaticWorkspace } from '../uiModels/workspace/useCreateAutomaticWorkspace'
import { useWorkspaceOperations } from '../uiModels/workspace/useWorkspaceOperations'
import { isEqualWorkspace } from './helpers'
import { type OperationsContext, Provider } from './operationsContext'
import { useShadowedTemplateOperations } from './useShadowedTemplateOperations'
import { useWorkspaceManagerContext } from './workspaceManagerContext'

const operationIsDraftCondition: Parameters<
  typeof useShadowedTemplateOperations
>[0]['operationCondition'] = ({ isDraft }) => !!isDraft

type Props = PropsWithChildren<{
  workspaceKey: Optional<WorkspaceKey>
  branchKey: Optional<BranchKey>
  isDisabledChangesetsRelevant: boolean
}>

const OperationsProvider = ({
  workspaceKey,
  branchKey,
  isDisabledChangesetsRelevant,
  children,
}: Props) => {
  const { getWorkspace, onSwitch, currentWorkspaceKey } =
    useWorkspaceManagerContext()
  const { removeOperation, addOrUpdateOperation } =
    useConfigurationComponentWorkspaces()
  const actualBranchKey = workspaceKey?.branchKey ?? branchKey
  const { server } = useNavigationParams()
  const { blockTopology } = useMetaModelContext()
  const rootBlock = blockTopology.name
  const conciseBranch = useConciseBranch({
    server: branchKey?.server ?? server,
    branchName: branchKey?.name,
    shouldFallbackToStandard: !!branchKey && branchKey.name === STANDARD_BRANCH,
  })
  const branchWorkspaces = useBaseBranchWorkspaces(branchKey)
  const [, createAutomaticWorkspace] = useCreateAutomaticWorkspace({
    workspaceNamePrefix: branchKey?.name ?? '',
    branchKey,
  })
  const conciseChangesets = useConciseChangesets({
    workspaceKey,
    branchKey,
    shouldIncludeBaseConciseChangesets: false,
  })

  const branchChangesetsExcludingFollowed = useMemo(
    () =>
      conciseChangesets.filter((conciseChangeset) =>
        isEqualBranch(conciseChangeset.branchKey, branchKey),
      ),
    [conciseChangesets, branchKey],
  )
  const branchChangesetKeysSet = useMemo(
    () => new Set(branchChangesetsExcludingFollowed.map((c) => c.changesetKey)),
    [branchChangesetsExcludingFollowed],
  )

  const workspace = useMemo(
    () => (workspaceKey ? getWorkspace(workspaceKey) : null),
    [getWorkspace, workspaceKey],
  )

  const { data: configurationChangesets, isLoading: isLoadingChangesets } =
    useChangesetsWithHistory({
      workspaceKey,
      branchKey:
        conciseBranch && actualBranchKey
          ? { name: actualBranchKey.name, server: actualBranchKey.server }
          : undefined,
      isDisabledChangesetsRelevant,
    })

  const changesetLayers = useMemo(
    () =>
      Array.from(
        new OperationLayerSet(
          (configurationChangesets ?? []).map(
            ({ changeset }) =>
              new LayerKey({
                tenantId: changeset?.tenantId,
                layerId: changeset?.layer,
              }),
          ),
        ).values(),
      ),
    [configurationChangesets],
  )

  const changesetBlocks = useMemo(
    () =>
      Array.from(
        new Set(
          (configurationChangesets ?? [])
            .map(({ block }) => block)
            .filter(notEmpty),
        ).values(),
      ),
    [configurationChangesets],
  )

  const { allLayers: allWorkspaceLayers, allBlocks: allWorkspaceBlocks } =
    useWorkspaceOperations(workspaceKey)
  const {
    data: draftTemplateOperations,
    isLoading: isLoadingTemplateOperations,
    filteredLayers: layersConfiguredOnWorkspace,
  } = useWorkspaceOperations(workspaceKey, isWorkspaceTemplateOperation)

  const {
    data: draftParameterDefinitionCreateOperations,
    isLoading: isLoadingParameterDefinitionOperations,
  } = useWorkspaceOperations(
    workspaceKey,
    isWorkspaceParameterDefinitionOperation,
  )

  const {
    data: draftParameterValueCreateOperations,
    isLoading: isLoadingParameterValueOperations,
  } = useWorkspaceOperations(workspaceKey, isWorkspaceParameterValueOperation)

  const {
    data: draftParameterValueAmendOperations,
    isLoading: isLoadingParameterValueAmendOperations,
  } = useWorkspaceOperations(
    workspaceKey,
    isWorkspaceParameterValueAmendOperation,
  )

  const {
    data: draftTenantCreateOperations,
    isLoading: isLoadingTenantOperations,
  } = useWorkspaceOperations(workspaceKey, isWorkspaceRegisteredTenantOperation)

  const { data: draftAnnulOperations, isLoading: isLoadingAnnulOperations } =
    useWorkspaceOperations<WorkspaceOperationOf<AnnulOperation>>(
      workspaceKey,
      isWorkspaceAnnulOperation,
    )

  const { data: draftGlMapOperations, isLoading: isLoadingGlMapOperations } =
    useWorkspaceOperations(workspaceKey, isWorkspaceGlMapOperation)

  const {
    data: draftReconciliationSpecCreateOperations,
    isLoading: isLoadingReconciliationSpecOperations,
  } = useWorkspaceOperations(
    workspaceKey,
    isWorkspaceReconciliationSpecCreateOperation,
  )

  const registeredTenantCreateOperations = useMemo(
    () =>
      constructIndexedRegisteredTenantCreateOperations({
        configurationChangesets,
        draftCreateOperations: draftTenantCreateOperations,
        draftAnnulOperations,
        rootBlock,
      }),
    [
      configurationChangesets,
      draftTenantCreateOperations,
      draftAnnulOperations,
      rootBlock,
    ],
  )

  const allAnnulOperationsByKey = useMemo(
    () =>
      buildAnnulOperationsByKey({
        changesets: configurationChangesets ?? [],
        draftAnnulOperations: draftAnnulOperations ?? [],
        rootBlock,
      }),
    [configurationChangesets, draftAnnulOperations, rootBlock],
  )

  const {
    errors: locationErrors,
    isLoading: isLoadingLocationErrors,
    workspaceValidationErrors,
    branchValidationErrors,
  } = useLocationErrors({
    enabled: true,
    workspaceKey,
    branchKey,
    allBlocks: Array.from(
      new Set([...changesetBlocks, ...allWorkspaceBlocks]).values(),
    ),
    allLayers: Array.from(
      new OperationLayerSet([
        ...allWorkspaceLayers,
        ...changesetLayers,
      ]).values(),
    ),
  })

  const configuredEnums = useConfiguredEnums({
    configurationChangesets,
    workspaceKey,
    errors: locationErrors,
    allTenants: registeredTenantCreateOperations,
    draftAnnulOperations,
  })

  const templateOperations = useMemo(
    () =>
      constructIndexedTemplateOperations({
        configurationChangesets,
        draftTemplateOperations: workspaceKey
          ? draftTemplateOperations
          : undefined,
        draftAnnulOperations,
        blockTopology,
      }),
    [
      configurationChangesets,
      draftTemplateOperations,
      draftAnnulOperations,
      workspaceKey,
      blockTopology,
    ],
  )

  // This is used in order to detect shadowed operations outside of the scope of the workspace, so we exclude workspaces operations and workspace annulments.
  const templateOperationsWithoutDraftsAndWorkspaceAnnulments = useMemo(
    () =>
      constructIndexedTemplateOperations({
        configurationChangesets,
        draftTemplateOperations: undefined,
        draftAnnulOperations: [],
        blockTopology,
      }),
    [configurationChangesets, blockTopology],
  )

  const workspaceShadowedTemplateOperations = useShadowedTemplateOperations({
    templateOperations,
    layers: layersConfiguredOnWorkspace,
    operationCondition: operationIsDraftCondition,
  })

  const operationInBranchCondition = useCallback(
    ({ changesetHistory }: IndexedTemplateOperationWithMetaData) =>
      branchChangesetKeysSet.has(changesetHistory?.changesetKey),
    [branchChangesetKeysSet],
  )

  const branchShadowedTemplateOperations = useShadowedTemplateOperations({
    templateOperations: templateOperationsWithoutDraftsAndWorkspaceAnnulments,
    layers: changesetLayers,
    operationCondition: operationInBranchCondition,
  })

  const parameterDefinitionCreateOperations = useMemo(
    () =>
      constructIndexedParameterDefinitionCreateOperations({
        configurationChangesets,
        draftCreateOperations: workspaceKey
          ? draftParameterDefinitionCreateOperations
          : undefined,
        draftAnnulOperations,
        blockTopology,
      }),
    [
      configurationChangesets,
      draftParameterDefinitionCreateOperations,
      draftAnnulOperations,
      workspaceKey,
      blockTopology,
    ],
  )

  const parameterValueEntityOperations = useMemo(
    () =>
      constructIndexedParameterValueOperations({
        configurationChangesets,
        draftValueOperations: workspaceKey
          ? [
              ...(draftParameterValueCreateOperations ?? []),
              ...(draftParameterValueAmendOperations ?? []),
            ]
          : undefined,
        draftAnnulOperations,
        rootBlock,
      }),
    [
      configurationChangesets,
      draftParameterValueCreateOperations,
      draftParameterValueAmendOperations,
      draftAnnulOperations,
      workspaceKey,
      rootBlock,
    ],
  )

  const glMapOperations = useMemo(
    () =>
      constructIndexedGlMapOperations({
        configurationChangesets,
        draftGlMapOperations: workspaceKey ? draftGlMapOperations : undefined,
        draftAnnulOperations,
        blockTopology,
      }),
    [
      configurationChangesets,
      draftGlMapOperations,
      draftAnnulOperations,
      workspaceKey,
      blockTopology,
    ],
  )

  const reconciliationSpecCreateOperations = useMemo(
    () =>
      constructIndexedReconciliationSpecOperations({
        configurationChangesets,
        draftReconciliationSpecOperations: workspaceKey
          ? draftReconciliationSpecCreateOperations
          : undefined,
        draftAnnulOperations,
        blockTopology,
      }),
    [
      configurationChangesets,
      draftReconciliationSpecCreateOperations,
      draftAnnulOperations,
      workspaceKey,
      blockTopology,
    ],
  )

  const handleAddOrUpdateOperation = async ({
    operation,
    layer,
    block,
    shouldOpenUnifiedBranchWorkspace,
  }: Parameters<OperationsContext['onAddOrUpdateOperation']>[0]) => {
    const singleWorkspaceKey =
      branchWorkspaces.length === 1
        ? conciseWorkspaceToWorkspaceKey({
            workspace: branchWorkspaces[0],
            server,
          })
        : null
    const writeToWorkspaceKey = workspaceKey ?? singleWorkspaceKey

    if (writeToWorkspaceKey) {
      await addOrUpdateOperation({
        block,
        operation,
        layer,
        workspaceKey: writeToWorkspaceKey,
      })

      if (
        shouldOpenUnifiedBranchWorkspace &&
        !isEqualWorkspace(writeToWorkspaceKey, currentWorkspaceKey)
      ) {
        await onSwitch(writeToWorkspaceKey, { mode: 'preserve' })
      }

      return writeToWorkspaceKey
    } else if (
      branchKey &&
      branchKey.name !== STANDARD_BRANCH &&
      !branchWorkspaces.length
    ) {
      const createdWorkspaceKey = await createAutomaticWorkspace({
        shouldInvalidateCache: true,
        shouldSwitchWorkspace: false,
      })

      if (shouldOpenUnifiedBranchWorkspace) {
        await onSwitch(createdWorkspaceKey, { mode: 'preserve' })
      }

      await addOrUpdateOperation({
        block,
        operation,
        layer,
        workspaceKey: createdWorkspaceKey,
      })

      return createdWorkspaceKey
    } else {
      throw new Error(
        'Could not update an operation. No workspace is currently in scope',
      )
    }
  }

  const handleRemoveOperation = async (operation: ChangeOperation) => {
    if (workspaceKey) {
      assertNotEmpty(operation.operationKey)
      await removeOperation(operation.operationKey, workspaceKey)
    } else {
      throw new Error(
        'Could not add an operation. No workspace is currently in scope',
      )
    }
  }

  return (
    <Provider
      value={{
        onAddOrUpdateOperation: handleAddOrUpdateOperation,
        onRemoveOperation: handleRemoveOperation,
        workspaceShadowedTemplateOperations,
        branchShadowedTemplateOperations,
        isLoading:
          isLoadingChangesets ||
          isLoadingTemplateOperations ||
          isLoadingParameterDefinitionOperations ||
          isLoadingParameterValueOperations ||
          isLoadingParameterValueAmendOperations ||
          isLoadingTenantOperations ||
          isLoadingGlMapOperations ||
          isLoadingReconciliationSpecOperations ||
          isLoadingAnnulOperations,
        isLoadingErrors: isLoadingLocationErrors,
        workspace,
        workspaceKey,
        branchKey,
        templateOperations,
        parameterDefinitionCreateOperations,
        parameterValueEntityOperations,
        registeredTenantCreateOperations,
        glMapOperations,
        reconciliationSpecCreateOperations,
        configuredEnums,
        allAnnulOperationsByKey,
        errors: locationErrors,
        branchValidationErrors,
        workspaceValidationErrors,
      }}
    >
      {children}
    </Provider>
  )
}

export { OperationsProvider }
